import React, { useState, useEffect, useRef } from 'react';

const STORAGE_KEY = 'kolqo_chat';

function Chat({ apiUrl, token, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);

  // CHARGER LOCALSTORAGE AU DEMARRAGE
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      } catch (e) {
        console.error('Erreur localStorage:', e);
      }
    }
    loadHistory();
  }, []);

  // SAUVEGARDER LOCALSTORAGE A CHAQUE CHANGEMENT
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  // SCROLL AUTO
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // MICRO AVEC GESTION HTTPS ET ERREURS
  const startListening = () => {
    // HTTPS obligatoire pour Web Speech API en production
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      alert('Le micro nécessite une connexion HTTPS.\nVérifiez que votre site utilise HTTPS (pas HTTP).');
      return;
    }

    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('La reconnaissance vocale n\'est pas supportée.\nUtilisez Chrome ou Edge à jour.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev + (prev ? ' ' : '') + transcript);
    };

    recognition.onerror = (event) => {
      console.error('Erreur reco vocale:', event.error);
      let msg = 'Erreur du micro.';
      if (event.error === 'not-allowed') {
        msg = 'Micro bloqué.\nCliquez sur l\'icône 🔒 dans la barre d\'adresse et autorisez le micro.';
      } else if (event.error === 'no-speech') {
        msg = 'Aucune voix détectée. Parlez plus fort ou plus près du micro.';
      } else if (event.error === 'network') {
        msg = 'Erreur réseau. Vérifiez votre connexion internet.';
      } else if (event.error === 'aborted') {
        msg = 'Reconnaissance annulée.';
      }
      alert(msg);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    try {
      recognition.start();
    } catch (e) {
      alert('Impossible de démarrer le micro. Réessayez.');
    }
  };

  // CHARGER L'HISTORIQUE SERVEUR (AVEC FUSION INTELLIGENTE)
  const loadHistory = async () => {
    try {
      const res = await fetch(`${apiUrl}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        // Si le serveur a des messages user (nouveau comportement), on utilise le serveur
        const hasUserMessages = data.messages.some(m => m.role === 'user');
        if (hasUserMessages) {
          setMessages(data.messages);
        }
        // Sinon on garde localStorage (ancien comportement où seul assistant était stocké)
      }
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userText }]);
    setLoading(true);

    try {
      const res = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: userText })
      });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          is_action: data.type === 'action'
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: '❌ Erreur serveur' }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ Erreur réseau. Vérifiez votre connexion.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  return (
    <div>
      <div className="chat-header">
        <h2>KOLQO ia</h2>
        <button className="btn-logout" onClick={onLogout}>Déconnexion</button>
      </div>
      <div className="messages">
        {messages.length === 0 && <div className="loading">Commence la conversation !</div>}
        {messages.map((msg, i) => (
          <div key={i} className={`message ${msg.role === 'user' ? 'message-user' : 'message-bot'}`}>
            <div className={`bubble ${msg.role === 'user' ? 'bubble-user' : 'bubble-bot'}`}>
              {msg.content}
              {msg.is_action && <div className="action-badge">Automatisation</div>}
            </div>
          </div>
        ))}
        {loading && <div className="loading">L'assistant réfléchit...</div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="input-area">
        <button 
          className={`btn-mic ${isListening ? 'listening' : ''}`} 
          onClick={startListening}
          title="Parler"
        >
          🎤
        </button>
        <input 
          type="text" 
          placeholder="Écris ou parle..." 
          value={input} 
          onChange={(e) => setInput(e.target.value)} 
          onKeyDown={handleKeyDown} 
          disabled={loading}
        />
        <button className="btn-send" onClick={sendMessage} disabled={loading}>
          Envoyer
        </button>
      </div>
    </div>
  );
}

export default Chat;